import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { URLS } from '@shared/constants';
import {
  CommonResponseDTO,
  IRoleResponse,
  IWorkflow,
  IWorkflowCanvasState,
} from '@shared/interfaces';

import { environment } from '../../../../environments/environment';

export interface WorkflowMenuItem {
  id: string;
  name: string;
  description: string;
  icon: string;
  virtualModuleName: string;
  hasEntryPoint: boolean;
  entryPointButtonText?: string;
  entryPointButtonIcon?: string;
  isPublished: boolean;
  permissions: string[];
}

export interface WorkflowFormField {
  id: string;
  name: string;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'select'
    | 'checkbox'
    | 'radio'
    | 'date'
    | 'number'
    | 'email'
    | 'file';
  required: boolean;
  options?: { value: string; label: string }[];
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
    customMessage?: string;
  };
  placeholder?: string;
  defaultValue?: any;
}

export interface WorkflowFormDefinition {
  fields: WorkflowFormField[];
  validation?: {
    customRules?: string[];
  };
  layout?: {
    columns?: number;
    sections?: {
      title: string;
      fields: string[];
      collapsible?: boolean;
    }[];
  };
}

export interface WorkflowStage {
  id: string;
  name: string;
  description: string;
  type: 'form' | 'approval' | 'notification' | 'action';
  order: number;
  formDefinition?: WorkflowFormDefinition;
  permissions?: {
    viewRoles: string[];
    editRoles: string[];
    approveRoles: string[];
    rejectRoles: string[];
  };
  actions?: {
    id: string;
    name: string;
    type: 'approve' | 'reject' | 'submit' | 'save' | 'custom';
    nextStageId?: string;
    permissions: string[];
    buttonStyle?: 'primary' | 'secondary' | 'danger' | 'success';
    confirmationMessage?: string;
  }[];
  nextStages?: string[];
  isEntryPoint?: boolean;
  isEndPoint?: boolean;
}

export interface WorkflowDefinition {
  _id: string;
  basicData: {
    name: string;
    description: string;
    virtualModuleName: string;
    moduleIcon: string;
  };
  workflow_builder: {
    stages: WorkflowStage[];
    entryPoint?: {
      stageId: string;
      showFloatingButton: boolean;
      buttonText?: string;
      buttonIcon?: string;
    };
    permissions?: {
      createRoles: string[];
      viewRoles: string[];
      editRoles: string[];
      deleteRoles: string[];
      executeRoles: string[];
    };
  };
}

export interface WorkflowInstance {
  _id: string;
  workflowId: string;
  title: string;
  currentStageId: string;
  status: 'draft' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  formData: Record<string, any>;
  stageHistory: {
    stageId: string;
    stageName: string;
    status: 'pending' | 'in_progress' | 'completed' | 'rejected' | 'skipped';
    startedAt: Date;
    completedAt?: Date;
    completedBy?: string;
    formData?: Record<string, any>;
    comments?: string;
    actions?: {
      actionId: string;
      actionName: string;
      actionType: string;
      performedAt: Date;
      performedBy: string;
      data?: Record<string, any>;
      comments?: string;
    }[];
  }[];
  assignedTo?: string;
  assignedBy?: string;
  dueDate?: Date;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: Record<string, any>;
  created_on: Date;
  last_modified_on: Date;
  changed_by: string;
}

export interface CreateWorkflowInstanceRequest {
  workflowId: string;
  title: string;
  formData?: Record<string, any>;
  assignedTo?: string;
  dueDate?: Date;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  metadata?: Record<string, any>;
}

export interface WorkflowStatistics {
  totalWorkflows: number;
  publishedWorkflows: number;
  draftWorkflows: number;
  workflowsWithEntryPoints: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowsService {
  private readonly apiUrl = `${environment.API_URL}/api/workflows`;
  private workflowRefresh$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  /**
   * Observable that emits when workflows are created/updated and sidemenu needs refresh
   */
  get onWorkflowRefresh(): Observable<void> {
    return this.workflowRefresh$.asObservable();
  }

  /**
   * Emit refresh event to notify sidemenu to update
   */
  notifyWorkflowRefresh(): void {
    this.workflowRefresh$.next();
  }

  // Workflow Management
  getAllWorkflows(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(`${this.apiUrl}`, { params: httpParams });
  }

  getWorkflowById(id: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/${id}`);
  }
  createWorkflow(workflow: IWorkflow): Observable<any> {
    return this.http.post(`${this.apiUrl}`, workflow);
  }

  updateWorkflow(id: string, workflow: IWorkflow): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}`, workflow);
  }

  deleteWorkflow(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  publishWorkflow(id: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/publish`, {});
  }

  unpublishWorkflow(id: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}/unpublish`, {});
  }

  // Menu Integration
  getPublishedWorkflowsForMenu(): Observable<{ data: WorkflowMenuItem[] }> {
    return this.http.get<{ data: WorkflowMenuItem[] }>(
      `${this.apiUrl}/menu/published`
    );
  }

  getWorkflowsWithEntryPoints(): Observable<{ data: WorkflowMenuItem[] }> {
    return this.http.get<{ data: WorkflowMenuItem[] }>(
      `${this.apiUrl}/menu/entry-points`
    );
  }

  getWorkflowByVirtualModuleName(
    virtualModuleName: string
  ): Observable<{ data: WorkflowMenuItem }> {
    return this.http.get<{ data: WorkflowMenuItem }>(
      `${this.apiUrl}/menu/by-virtual-module/${virtualModuleName}`
    );
  }

  getWorkflowStatistics(): Observable<{ data: WorkflowStatistics }> {
    return this.http.get<{ data: WorkflowStatistics }>(
      `${this.apiUrl}/statistics`
    );
  }

  // Workflow Instances
  getAllWorkflowInstances(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}`,
      { params: httpParams }
    );
  }

  getWorkflowInstanceById(id: string): Observable<any> {
    return this.http.get(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}/${id}`
    );
  }

  createWorkflowInstance(
    instance: CreateWorkflowInstanceRequest
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}`,
      instance
    );
  }

  updateWorkflowInstance(id: string, instance: any): Observable<any> {
    return this.http.put(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}/${id}`,
      instance
    );
  }

  deleteWorkflowInstance(id: string): Observable<any> {
    return this.http.delete(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}/${id}`
    );
  }

  updateWorkflowInstanceStage(id: string, stageData: any): Observable<any> {
    return this.http.patch(
      `${this.apiUrl.replace('/workflows', '/workflow-instances')}/${id}/stage`,
      stageData
    );
  }

  getInstancesByWorkflow(workflowId: string, params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(
      `${this.apiUrl.replace(
        '/workflows',
        '/workflow-instances'
      )}/workflow/${workflowId}`,
      { params: httpParams }
    );
  }

  getMyInstances(params?: any): Observable<any> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return this.http.get(
      `${this.apiUrl.replace(
        '/workflows',
        '/workflow-instances'
      )}/my-instances`,
      { params: httpParams }
    );
  }

  //added by shashika
  public getUsedIconList(): Observable<CommonResponseDTO<string[]>> {
    const url = `${URLS.WORKFLOWS}/icons`;
    return this.http.get<CommonResponseDTO<string[]>>(url);
  }

  public getWorkFlow(id: string): Observable<CommonResponseDTO<IWorkflow>> {
    const url = `${URLS.WORKFLOWS}/${id}`;
    return this.http.get<CommonResponseDTO<IWorkflow>>(url);
  }

  checkModuleNameUniqueness(
    params: HttpParams
  ): Observable<CommonResponseDTO<IWorkflow[]>> {
    const url = `${URLS.WORKFLOWS}/moduleNameValidator`;

    const config = { params };

    return this.http.get<CommonResponseDTO<IWorkflow[]>>(url, config);
  }

  public DuplicateWorkflow(
    id: string
  ): Observable<CommonResponseDTO<IWorkflow>> {
    const url = `${URLS.WORKFLOWS}/${id}/duplicate`;
    return this.http.patch<CommonResponseDTO<IWorkflow>>(url, {});
  }

  public getAllWorkFlowsForSidePanel(): Observable<
    CommonResponseDTO<IWorkflow[]>
  > {
    return this.http.get<CommonResponseDTO<IWorkflow[]>>(
      URLS.WORKFLOWS_SIDE_PANEL
    );
  }

  // Additional methods from WorkflowService
  public getAllWorkFlows(
    params: HttpParams
  ): Observable<CommonResponseDTO<IWorkflow[]>> {
    const config = { params };
    return this.http.get<CommonResponseDTO<IWorkflow[]>>(
      URLS.WORKFLOWS,
      config
    );
  }

  public createDraftWorkFlow(
    notice: IWorkflow
  ): Observable<CommonResponseDTO<IWorkflow>> {
    return this.http.post<CommonResponseDTO<IWorkflow>>(
      URLS.WORKFLOWS_DRAFT,
      notice
    );
  }

  public softDeleteWorkFlow(id: string) {
    const url = `${URLS.WORKFLOWS}/${id}`;
    return this.http.delete<CommonResponseDTO<IWorkflow>>(url);
  }

  public restoreWorkFlow(id: string): Observable<CommonResponseDTO<IWorkflow>> {
    const url = `${URLS.WORKFLOWS}/${id}/restore`;
    return this.http.patch<CommonResponseDTO<IWorkflow>>(url, {});
  }

  public ActivateWorkflow(
    id: string
  ): Observable<CommonResponseDTO<IWorkflow>> {
    const url = `${URLS.WORKFLOWS}/${id}/activate`;
    return this.http.patch<CommonResponseDTO<IWorkflow>>(url, {});
  }

  public DeactivateWorkflow(
    id: string
  ): Observable<CommonResponseDTO<IWorkflow>> {
    const url = `${URLS.WORKFLOWS}/${id}/deactivate`;
    return this.http.patch<CommonResponseDTO<IWorkflow>>(url, {});
  }

  /**
   * Save workflow with complete node configurations from canvas state
   * This method saves canvas state directly in workflow_builder (not nested)
   */
  public saveWorkflowWithNodeConfigurations(
    workflowId: string | null,
    basicWorkflowData: IWorkflow,
    workflowCanvasState: IWorkflowCanvasState
  ): Observable<CommonResponseDTO<IWorkflow>> {
    try {
      // The nodes in workflowCanvasState should already have their configurations
      const nodesWithConfigs = workflowCanvasState.nodes.map((node) => {
        return {
          ...node,
          config: node.config || {},
        };
      });

      // Create complete workflow data with canvas state directly in workflow_builder
      const completeWorkflowData: IWorkflow = {
        ...basicWorkflowData,
        workflow_builder: {
          nodes: nodesWithConfigs,
          connections: workflowCanvasState.connections || [],
        },
      };

      // Save to database
      if (workflowId) {
        return this.updateWorkflow(workflowId, completeWorkflowData);
      } else {
        return this.createWorkflow(completeWorkflowData);
      }
    } catch (error) {
      console.error('Error preparing workflow data for save:', error);
      throw error;
    }
  }

  /**
   * Extract canvas state from workflow data for loading
   */
  public extractCanvasStateFromWorkflow(
    workflowData: any
  ): IWorkflowCanvasState | null {
    try {
      const workflowBuilder = workflowData?.workflow_builder;

      if (!workflowBuilder) {
        return null;
      }

      // Check if canvas state is stored directly in workflow_builder (new format)
      if (workflowBuilder.nodes && workflowBuilder.connections) {
        return {
          nodes: workflowBuilder.nodes || [],
          connections: workflowBuilder.connections || [],
        };
      }

      // Check if canvas state is nested under workflow_canvas_state (old format)
      if (workflowBuilder.workflow_canvas_state) {
        return workflowBuilder.workflow_canvas_state;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  // Get all roles from API for Permissions
  getAllRoles(): Observable<CommonResponseDTO<IRoleResponse[]>> {
    return this.http.get<CommonResponseDTO<IRoleResponse[]>>(URLS.ROLES);
  }
}
